import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Building2, IdCard, CalendarClock, Copy, CheckSquare, Download, ShieldCheck, ScrollText,
  DatabaseZap, Plug, FileStack, Lock, Bell, Search, LogOut, Menu, X, KeyRound, Settings2, MessageCircle, PhoneCall, Home, Trash2, Trophy, Factory, ListChecks, Gauge, BadgeCheck, SlidersHorizontal, FileSpreadsheet, ListOrdered, Target, ClipboardList, LineChart, CalendarDays, Eye, BellRing, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useAlertPrefs, inHours, typeOf, popupOn, soundOn, chime, setMuted } from "../lib/alerts";
import { Watermark, useSecurityDeterrents } from "./Security";
import { BRANCH_LABEL, BOARD_ROLES, BIN_ROLES } from "./common";
import { LeadNudger } from "./LeadNudger";
import { LucyOrb, LucyAvatar } from "./bp/Lucy";

const BP_LEADS = ["founder", "branch_manager", "_legacy_branch_manager", "managing_partner", "sales_manager"];
const has = (u, m, a) => !!u?.modules?.[m]?.actions?.includes(a);

const NAV = [
  { group: "Stage 1", items: [{ to: "/blueprint", label: "Blueprint", icon: FileStack, show: () => true }] },
  { group: "Sales & Leads", items: [
    { to: "/", label: "Home", icon: Home, perm: ["sales", "read"] },
    { to: "/sales/dashboard", label: "Sales Dashboard", icon: LayoutDashboard, perm: ["sales", "read"] },
    { to: "/brief", label: "Founders Brief", icon: FileStack, founder: true },
    { to: "/sales/leads", label: "Leads", icon: Users, perm: ["sales", "read"] },
    { to: "/sales/callbacks", label: "Call back now", icon: PhoneCall, perm: ["sales", "read"] },
    { to: "/sales/followups", label: "Follow-ups", icon: CalendarClock, perm: ["sales", "read"] },
    { to: "/sales/leaderboard", label: "Leaderboard", icon: Trophy, show: (u) => BOARD_ROLES.includes(u?.role_key) },
    { to: "/sales/accounts", label: "Accounts", icon: Building2, perm: ["sales", "read"] },
    { to: "/sales/duplicates", label: "Duplicate Review", icon: Copy, perm: ["sales", "read"] },
    { to: "/sales/approvals", label: "Approvals", icon: CheckSquare, perm: ["sales", "read"] },
    { to: "/sales/deleted", label: "Deleted Bin", icon: Trash2, show: (u) => BIN_ROLES.includes(u?.role_key) },
    { to: "/sales/exports", label: "Exports", icon: Download, perm: ["sales", "export"] },
  ] },
  { group: "Order Management", items: [
    { to: "/orders/dashboard", label: "Order Dashboard", icon: LayoutDashboard, perm: ["orders", "read"] },
    { to: "/orders/list", label: "UID Tracker", icon: FileStack, perm: ["orders", "read"] },
    { to: "/orders/document-log", label: "Document Log", icon: Eye, show: (u) => ["founder", "managing_partner"].includes(u?.role_key) },
  ] },
  { group: "Operations", items: [
    { to: "/ops/my-tasks", label: "My Tasks", icon: ListChecks, perm: ["operations", "read"] },
    { to: "/my-steps", label: "My Tasks", icon: ListChecks, show: (u) => !has(u, "operations", "read") && !has(u, "sales", "read") },
    { to: "/ops", label: "Production Board", icon: Factory, perm: ["operations", "read"] },
    { to: "/ops/templates", label: "Step Templates", icon: ListOrdered, perm: ["operations", "read"] },
    { to: "/ops/scores", label: "On-time & Late Scores", icon: Gauge, perm: ["operations", "read"] },
    { to: "/ops/approvals", label: "Ops Approvals", icon: BadgeCheck, perm: ["operations", "create"] },
    { to: "/ops/settings", label: "Shifts & Steps", icon: SlidersHorizontal, show: (u) => ["founder", "production_head"].includes(u?.role_key) },
    { to: "/ops/import", label: "Import Trackers", icon: FileSpreadsheet, show: (u) => ["founder", "production_head", "managing_partner", "execution_team", "production_coordinator"].includes(u?.role_key) },
  ] },
  { group: "BP & Planning", items: [
    { to: "/bp", label: "BP Dashboard", icon: LineChart, show: (u) => !!(u?.modules?.sales?.actions?.includes("read") || u?.modules?.bp?.actions?.includes("read")) },
    { to: "/bp/branch", label: "Branch Plan", icon: Target, show: (u) => BP_LEADS.includes(u?.role_key) },
    { to: "/bp/my-plan", label: "Sales Plans", icon: ClipboardList, perm: ["sales", "read"] },
    { to: "/bp/week", label: "Weekly Review", icon: CalendarDays, show: (u) => !!(u?.modules?.sales?.actions?.includes("read") || u?.modules?.bp?.actions?.includes("read")) },
  ] },
  { group: "Later stages", items: [
    { to: "/locked/founder", label: "Founder Dashboard", icon: Lock, show: () => true, locked: true },
  ] },
  { group: "People", items: [
    { to: "/hr/employees", label: "Employee Records", icon: IdCard, perm: ["hr", "read"] },
  ] },
  { group: "Administration", items: [
    { to: "/admin/users", label: "Users & Access", icon: ShieldCheck, perm: ["admin", "manage_users"] },
    { to: "/admin/access-check", label: "Access Check", icon: ShieldCheck, perm: ["admin", "manage_access"] },
    { to: "/admin/roles", label: "Role Templates", icon: KeyRound, perm: ["admin", "manage_access"] },
    { to: "/admin/audit", label: "Audit Log", icon: ScrollText, perm: ["admin", "view_audit"] },
    { to: "/admin/migration", label: "Migration", icon: DatabaseZap, perm: ["sales", "import"] },
    { to: "/admin/intake", label: "Lead Intake", icon: Plug, perm: ["admin", "read"] },
    { to: "/admin/whatsapp", label: "WhatsApp & Files", icon: MessageCircle, perm: ["admin", "update"] },
    { to: "/admin/alerts", label: "Reminders & Alerts", icon: BellRing, show: (u) => ["founder", "system_admin"].includes(u?.role_key) },
    { to: "/admin/settings", label: "Sales Settings", icon: Settings2, perm: ["admin", "approve"] },
  ] },
];

const useDueCallbacks = (enabled) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/sales/callbacks/count").then((r) => setN(r.data.due)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("callbacks-changed", load);
    return () => { clearInterval(t); window.removeEventListener("callbacks-changed", load); };
  }, [enabled]);
  return n;
};

const SideNav = ({ onNav }) => {
  const { can, user } = useAuth();
  const due = useDueCallbacks(can("sales", "read"));
  return (
    <nav className="flex-1 overflow-y-auto py-4">
      {NAV.map((g) => {
        const items = g.items.filter((i) => (i.founder ? user?.role_key === "founder" : i.show ? i.show(user) : can(...i.perm)));
        if (!items.length) return null;
        return (
          <div key={g.group} className="mb-5">
            <div className="ax-label px-5 mb-2">{g.group}</div>
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.to === "/" || i.to === "/ops" || i.to === "/bp"} onClick={onNav} data-testid={i.to === "/" ? "nav-home" : `nav-${i.to.replaceAll("/", "-").slice(1)}`}
                className={({ isActive }) => `flex items-center gap-3 px-5 py-2 text-sm border-l-2 transition-colors ${isActive ? "border-primary bg-muted text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"} ${i.locked ? "opacity-60" : ""}`}>
                <i.icon className="h-4 w-4" /> {i.label}
                {i.to === "/sales/callbacks" && due > 0 && <span data-testid="nav-callbacks-badge" className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-xs num flex items-center justify-center">{due > 99 ? "99+" : due}</span>}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
};

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (path) => { setQ(""); setRes(null); nav(path); };
  return (
    <div className="relative w-full max-w-md">
      <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads, companies, IDs…"
        className="w-full bg-muted/60 border h-9 pl-9 pr-3 text-sm outline-none focus:border-primary" />
      {res && (
        <div data-testid="global-search-results" className="absolute top-10 left-0 right-0 z-50 ax-panel shadow-2xl max-h-96 overflow-y-auto">
          {[...res.leads.map((l) => ({ k: l.lead_id, t: `${l.company_name || l.contact_name || "—"}`, s: `${l.lead_id} · ${l.owner || ""}`, p: `/sales/leads/${l.lead_id}` })),
            ...res.accounts.map((a) => ({ k: a.account_id, t: a.name, s: `${a.account_id} · Account`, p: `/sales/accounts/${a.account_id}` }))].map((x) => (
            <button key={x.k} onClick={() => go(x.p)} data-testid={`search-result-${x.k}`} className="block w-full text-left px-3 py-2 hover:bg-muted border-b border-border/50">
              <div className="text-sm">{x.t}</div><div className="text-xs text-muted-foreground font-mono">{x.s}</div>
            </button>
          ))}
          {!res.leads.length && !res.accounts.length && <div className="p-3 text-sm text-muted-foreground">No matches you can access.</div>}
        </div>
      )}
    </div>
  );
};

const TOAST_KINDS = { file_open: "Open lead", callback: "Open lead", brief: "Open brief", delay: "Open UIDs", task: "Open lead", uid_event: "Open UID", new_lead: "Open lead", ops_task: "Open my tasks", ops_qc: "Open step", ops_reminder: "Open my tasks", uid_po: "Open UID", ops_escalation: "Open board", ops_request: "Open approvals", ops_decision: "Open", ops_plan: "Open plan", partner_brief: "Open board", file_incomplete: "Re-upload", handover: "Open list", lucy_plan: "Review meetings", lucy_recap: "Open meetings", lucy_nudge: "Open winning sheet", nudge: "Open" };

const isFresh = (n, seen) => TOAST_KINDS[n.kind] && !n.read && (seen ? !seen.has(n.notification_id) : Date.now() - new Date(n.at).getTime() < 15 * 60000);

const allowed = (n, pf) => {
  const t = typeOf(pf, n.kind);
  return !t || (popupOn(pf, t) && inHours(pf));
};

const useNotifications = (nav) => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const seen = useRef(null);
  const navRef = useRef(nav);
  navRef.current = nav;
  const prefs = useAlertPrefs();
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const load = useCallback(() => api.get("/notifications").then((r) => {
    const pf = prefsRef.current;
    const fresh = r.data.items.filter((n) => isFresh(n, seen.current) && allowed(n, pf));
    if (fresh.some((n) => soundOn(pf, typeOf(pf, n.kind)))) chime();
    if (fresh.some((n) => n.kind === "delay" || n.kind === "task")) navigator.vibrate?.([200, 100, 200]);
    fresh.forEach((n) => toast.success(n.title, { id: n.kind === "callback" && n.link ? `cb-${n.link}` : n.notification_id, description: n.body, action: n.link ? { label: TOAST_KINDS[n.kind], onClick: () => navRef.current(n.link) } : undefined, duration: 15000 }));
    seen.current = new Set(r.data.items.map((n) => n.notification_id));
    setData(r.data);
  }).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  return { data, load };
};

const BellPanel = ({ items, onPick }) => (
  <div className="fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-80 z-50 ax-panel shadow-2xl max-h-96 overflow-y-auto" data-testid="notifications-panel">
    {items.length === 0 && <div className="p-4 text-sm text-muted-foreground">No notifications.</div>}
    {items.map((n) => (
      <button key={n.notification_id} onClick={() => onPick(n)} className="flex gap-2 w-full text-left px-3 py-2 border-b border-border/50 hover:bg-muted">
        {n.kind?.startsWith("lucy") && <LucyAvatar size="h-7 w-7 mt-0.5" tid={`notification-lucy-${n.notification_id}`} />}
        <span className="min-w-0"><span className="block text-sm">{n.title}</span><span className="block text-xs text-muted-foreground">{n.body}</span></span>
      </button>
    ))}
  </div>
);

const Bellbox = () => {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { data, load } = useNotifications(nav);
  const toggle = () => { setOpen(!open); if (!open && data.unread) api.post("/notifications/read").then(load); };
  const pick = (n) => { setOpen(false); if (n.link) nav(n.link); };
  return (
    <div className="relative">
      <button data-testid="notifications-bell" onClick={toggle} className="relative h-9 w-9 border flex items-center justify-center hover:border-primary">
        <Bell className="h-4 w-4" />
        {data.unread > 0 && <span data-testid="notifications-unread" className="absolute -top-1 -right-1 bg-primary text-[10px] px-1 num">{data.unread}</span>}
      </button>
      {open && <BellPanel items={data.items} onPick={pick} />}
    </div>
  );
};

const SoundToggle = () => {
  const pf = useAlertPrefs();
  if (!pf) return null;
  return (
    <button data-testid="sound-toggle" onClick={() => setMuted(!pf.muted)} className="mt-2 flex items-center gap-2 text-muted-foreground hover:text-primary">
      {pf.muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />} {pf.muted ? "Reminder sound off" : "Reminder sound on"}</button>
  );
};

export const AppShell = () => {
  const { user, logout } = useAuth();
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  useSecurityDeterrents();
  return (
    <div className="min-h-screen flex">
      <aside className={`fixed lg:static z-50 inset-y-0 left-0 w-64 ax-side border-r flex flex-col transition-transform ${mobile ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-14 flex items-center justify-between px-5 border-b">
          <div className="font-display font-bold tracking-tight text-lg">AXIOM<span className="text-primary">/</span>ONE</div>
          <button className="lg:hidden" onClick={() => setMobile(false)} data-testid="close-mobile-nav"><X className="h-4 w-4" /></button>
        </div>
        <SideNav onNav={() => setMobile(false)} />
        <div className="border-t p-4 text-xs">
          <div className="font-medium text-sm truncate" data-testid="current-user-email">{user?.email}</div>
          <div className="text-muted-foreground mt-0.5" data-testid="current-user-role">{user?.role_name || user?.role_key}{user?.role_key !== "managing_partner" && user?.branches?.length ? ` · ${user.branches.map((b) => BRANCH_LABEL[b] || b).join(", ")}` : ""}</div>
          <NavLink to="/account/password" data-testid="change-password-link" onClick={() => setMobile(false)} className="mt-3 flex items-center gap-2 text-muted-foreground hover:text-primary"><KeyRound className="h-3.5 w-3.5" /> {user?.has_password ? "Change password" : "Create a password"}</NavLink>
          <SoundToggle />
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }} className="mt-2 flex items-center gap-2 text-muted-foreground hover:text-primary"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b flex items-center gap-3 px-4 lg:px-8 sticky top-0 z-30 bg-background/85 backdrop-blur-md">
          <button className="lg:hidden" onClick={() => setMobile(true)} data-testid="open-mobile-nav"><Menu className="h-5 w-5" /></button>
          <GlobalSearch />
          <div className="flex-1" />
          <span className="hidden md:inline-flex items-center gap-1.5 text-xs text-emerald-400 border border-emerald-500/30 rounded-full px-2.5 py-1" data-testid="security-status"><ShieldCheck className="h-3.5 w-3.5" /> Session secured · audited</span>
          <LucyOrb />
          <Bellbox />
          <img src="/axiom-logo.webp" alt="Axiom" className="h-6 sm:h-7 w-auto ml-1 select-none" draggable="false" data-testid="header-axiom-logo" />
        </header>
        <main data-module={loc.pathname.split("/")[1] || "home"} className="flex-1 p-4 lg:p-8 relative ax-shell-main"><Outlet /></main>
      </div>
      <Watermark />
      <LeadNudger />
    </div>
  );
};
